export function formatServiceDate(date: Date): string[] {
    const dateTimeFormat = new Intl.DateTimeFormat('en-US', {
        weekday: "short",
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        // timeZone: dateString.tzid,
    });
    const parts = dateTimeFormat.formatToParts(date);
    return parts.map((p) => p.value).reduce((out, curr) => {
        if (!curr.trim().length) {
            if (out[out.length - 1]) {
                out.push(" ");
                out.push("");
            }
        } else {
            out[out.length - 1] += curr;
        }
        return out;
    }, [""]);
}

export function formatNoteDate(date: Date): string[] {
    const dateTimeFormat = new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        // timeZone: dateString.tzid,
    });
    const parts = dateTimeFormat.formatToParts(date);
    return parts.map((p) => p.value).reduce((out, curr) => {
        if (curr.trim() === "," || !curr.trim().length) {
            if (out[out.length - 1]) {
                out.push(" ");
                out.push("");
            }
        } else {
            out[out.length - 1] += curr;
        }
        return out;
    }, [""]);
}

export function formatNoteDay(date: Date): string[] {
    const dateTimeFormat = new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
    });
    const parts = dateTimeFormat.formatToParts(date);
    return parts.map((p) => p.value).reduce((out, curr) => {
        if (curr.trim() === "," || !curr.trim().length) {
            if (out[out.length - 1]) {
                out.push(" ");
                out.push("");
            }
        } else {
            out[out.length - 1] += curr;
        }
        return out;
    }, [""]);
}

export function formatNormieTime(date: Date): string[] {
    const dateTimeFormat = new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
        // timeZone: dateString.tzid,
    });
    const parts = dateTimeFormat.formatToParts(date);
    return parts.map((p) => p.value).reduce((out, curr) => {
        if (curr.trim() === "," || !curr.trim().length) {
            if (out[out.length - 1]) {
                out.push(" ");
                out.push("");
            }
        } else {
            out[out.length - 1] += curr;
        }
        return out;
    }, [""]);
}

export function formatNormieTimeShorter(date: Date): string[] {
    return formatNormieTime(date).map(it => it.match(/^[ap]\.?m\.?$/i) ? it[0] : it);
}

export function sToHM(seconds: number) {
    return `${Math.floor(seconds / 3600)}:${(Math.floor(seconds / 60) % 60).toFixed(0).padStart(2, '0')}`
}

/**
 * Formats a date into ISO-ish format with time (local timezone)
 * @param date - The date to format
 * @returns Formatted date string in format YYYY-MM-DDTHH:mm
 */
export function formatISOishDate(date: Date | null | undefined): string {
    if (!date) return "";
    return `${formatISOYMD(date)}T${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
}

/**
 * Formats a date into ISO YYYY-MM-DD format (local timezone)
 * @param date - The date to format
 * @returns Formatted date string in format YYYY-MM-DD
 */
export function formatISOYMD(date: Date | null | undefined): string {
    if (!date) return "";
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${(date.getDate()).toString().padStart(2, '0')}`;
}

/**
 * Formats a UTC date into ISO YYYY-MM-DD format
 * @param date - The date to format
 * @returns Formatted date string in format YYYY-MM-DD (UTC)
 */
export function formatISOUTCYMD(date: Date | null | undefined): string {
    if (!date) return "";
    return `${date.getUTCFullYear()}-${(date.getUTCMonth() + 1).toString().padStart(2, '0')}-${(date.getUTCDate()).toString().padStart(2, '0')}`;
}

/**
 * Formats a date into ISO-ish UTC format with time
 * @param date - The date to format
 * @returns Formatted date string in format YYYY-MM-DDTHH:mm (UTC)
 */
export function formatISOishUTCDate(date: Date | null | undefined): string {
    if (!date) return "";
    return `${formatISOUTCYMD(date)}T${date.getUTCHours().toString().padStart(2, '0')}:${date.getUTCMinutes().toString().padStart(2, '0')}`;
}

/**
 * Formats a date into Time of Day format (local timezone)
 * @param date - The date to format
 * @returns Formatted date string in format M/DD HH:mm
 */
export function formatTOD(date: Date): string {
    const dateTimeFormat = new Intl.DateTimeFormat('en-US', {
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        // timeZone: dateString.tzid,
    });
    const parts = dateTimeFormat.formatToParts(date);

    // console.log(parts);

    return parts.map((p) => p.value).reduce((out, curr) => {
        if (curr.trim() === "," || !curr.trim().length) {
            if (out[out.length - 1]) {
                out.push(" ");
                out.push("");
            }
        } else {
            out[out.length - 1] += curr;
        }
        return out;
    }, [""]).join("");
}

/**
 * Converts a raw duration (in seconds) to HH:MM format
 * @param rawDuration - Duration in seconds (can be negative)
 * @returns Formatted duration string in format -HH:MM or HH:MM
 */
export function toHM(rawDuration: number): string {
    let duration = Math.abs(rawDuration);
    return `${rawDuration < 0 ? '-' : ''}${Math.floor(duration / 3600)}:${(Math.floor(duration / 60) % 60).toString().padStart(2, '0')}`;
}

/**
 * Converts a raw duration (in seconds) to HH:MM:SS format
 * @param rawDuration - Duration in seconds (can be negative)
 * @returns Formatted duration string in format -HH:MM:SS or HH:MM:SS
 */
export function toHMS(rawDuration: number): string {
    let duration = Math.abs(rawDuration);
    return toHM(duration) + `:${(Math.floor(duration / 3600) % 60).toString().padStart(2, '0')}`;
}
